<template>
    <div class="sub-container">
        <main>
            <ModifyTitle
                id="modify-title"
                :key="designatorRenderKey"
                class="title-data box-shadow grid-item"
                showViewAllListingsLink
                v-bind="{ ...currentTitle, designators: localDesignators }"
                @changes="handleTitleChanges"
                @removal="removeTitleModifications"
            />

            <DigitalPrices
                id="digital-pricing"
                :productCode="currentTitle.productCode"
                class="box-shadow"
            />

            <SlingshotLibraries
                :selected-libraries="slingshotLibraries"
                :titleIsbn="currentTitle.productCode"
                class="slingshot-libraries grid-item box-shadow"
            />

            <VendorLibraries
                :selected-libraries="vendorLibraries"
                :titleIsbn="currentTitle.productCode"
                class="vendor-libraries grid-item box-shadow"
            />

            <SlingshotAccessCode
                :hasSlingshotDesignator="hasSlingshotDesignator"
                :duration.sync="slingshotDuration"
                :instructions.sync="slingshotInstructions"
                :durationError="durationError"
                class="slingshot-access-code grid-item"
            />
            <!-- Inclusive Access Pricing -->
            <InclusiveAccessPrices
                id="ia-pricing"
                :title="currentTitle"
            />
        </main>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import DigitalPrices from '@/views/common/DigitalPrices';
import InclusiveAccessPrices from '@/views/common/InclusiveAccessPrices';
import ModifyTitle from '@/views/common/ModifyTitle';
import SlingshotLibraries from '@/components/multioption/SlingshotLibraries';
import VendorLibraries from '@/components/multioption/VendorLibraries';
import SlingshotAccessCode from '@/components/multioption/SlingshotAccessCode';

export default {
    name: 'TitleDetail',
    components: {
        VendorLibraries,
        SlingshotLibraries,
        DigitalPrices,
        SlingshotAccessCode,
        InclusiveAccessPrices,
        ModifyTitle,
    },
    data() {
        return {
            slingshotDuration: null,
            slingshotInstructions: null,
            localDesignators: [],
            confirmedDesignators: [],
            showDesignatorDialog: false,
            designatorDialogMessage: '',
            pendingDesignatorChange: null,
            designatorRenderKey: 0,
            durationError: false,
        };
    },
    computed: {
        slingshotLibraries() {
            return this.selectedLibraries.filter(({ manuallyManaged }) => manuallyManaged);
        },
        vendorLibraries() {
            return this.selectedLibraries.filter(({ manuallyManaged }) => !manuallyManaged);
        },
        hasSlingshotDesignator() {
            return this.localDesignators.some(
                (designator) => designator.name === 'SLINGSHOT_DIGITAL_ACCESS_CODE',
            );
        },
        ...mapState({
            currentTitle: (state) => state.currentTitle,
            selectedLibraries: (state) => state.Title.LibraryManagement.selectedLibraries,
        }),
    },
    watch: {
        currentTitle: {
            immediate: true,
            handler(newValue) {
                if (!newValue) return;

                this.initializeTitleModifications(newValue);

                this.slingshotDuration = newValue.slingshotDigitalAccessCodeDuration;

                this.slingshotInstructions = newValue.slingshotDigitalAccessCodeInstructions;

                // Initialize once per title load
                this.localDesignators = [...(newValue.designators || [])];
                this.confirmedDesignators = [...this.localDesignators];
            },
        },
        slingshotDuration(value) {
            // Force integer and min -1
            if (value !== null && value !== '') {
                let sanitized = Math.floor(Number(value));
                if (sanitized < -1) sanitized = -1;
                this.slingshotDuration = sanitized;
            }

            this.updateTitleModifications({
                key: 'slingshotDigitalAccessCodeDuration',
                value: this.slingshotDuration,
            });

            // Clear error if valid duration is set
            if (this.slingshotDuration !== null && this.slingshotDuration !== '' && this.slingshotDuration !== 0) {
                this.durationError = false;
            }
        },
        slingshotInstructions(value) {
            this.updateTitleModifications({
                key: 'slingshotDigitalAccessCodeInstructions',
                value,
            });
        },
    },
    beforeMount() {
        this.initializeGlobalResources();
        this.fetchSelectedLibraries(this.currentTitle.productCode);
    },
    methods: {
        handleTitleChanges(change) {
            if (change.key !== 'designators') {
                this.updateTitleModifications(change);
                return;
            }

            // Handle designator changes - delegate to validation method
            this.validateSlingshotDesignatorChange(change.value);
        },

        validateSlingshotDesignatorChange(nextDesignators) {
            const hasSlingshotNow = nextDesignators.some(
                (d) => d.name === 'SLINGSHOT_DIGITAL_ACCESS_CODE',
            );

            // Apply designator change immediately without validation
            this.localDesignators = [...nextDesignators];
            this.updateTitleModifications({
                key: 'designators',
                value: this.localDesignators,
            });

            // Clear any existing error when designator is deselected
            if (!hasSlingshotNow) {
                this.durationError = false;
            }
        },

        validateBeforeSave() {
            if (!this.hasSlingshotDesignator) return true;

            const duration = this.slingshotDuration;

            // Check empty / zero
            if (duration === null || duration === '' || duration === 0) {
                this.durationError = true;
                this.$store.dispatch(
                    'setErrorNotification',
                    'Duration is required when Slingshot Access Code designator is selected',
                    { root: true },
                );
                return false;
            }

            this.durationError = false;
            return true;
        },
        ...mapActions({
            initializeGlobalResources: 'GlobalResources/init',
            initializeTitleModifications: 'Title/Queue/initializeTitleModifications',
            updateTitleModifications: 'Title/Queue/updateTitleModifications',
            removeTitleModifications: 'Title/Queue/removeTitleModifications',
            fetchSelectedLibraries: 'Title/LibraryManagement/fetchSelectedLibraries',
        }),
    },
};
</script>

<style lang="scss" scoped>
.sub-container {
    ol,
    ul {
        list-style: none;
        margin: 0;
        padding: 0;
    }

    h2 {
        font-variant-caps: all-small-caps;
    }

    main {
        display: grid;
        grid-template:
            "title-data digital-pricing"
            "title-data slingshot-access-code"
            "title-data slingshot-libraries"
            "title-data vendor-libraries"
            "ia-pricing ia-pricing"
            / 1fr 1fr;
        grid-gap: 20px;
        grid-column-gap: 1%;
        margin-bottom: 10rem;
    }

    .grid-item {
        padding: 1rem;
    }

    .slingshot-access-code {
        grid-area: slingshot-access-code;
        padding: 0;
    }

    .title-data {
        grid-area: title-data;
    }

    #digital-pricing {
        grid-area: digital-pricing;
    }

    #ia-pricing {
        grid-area: ia-pricing;
    }

    &__title {
        &--details {
            display: flex;
            justify-content: left;

            .isbn,
            .book-title,
            .supplier {
                margin-right: 3rem;
            }
        }

        h2 {
            display: inline-block;
        }
    }
}
</style>
